import React, { Component, createRef, RefObject } from "react";
import { isMobile } from "react-device-detect";
import SimpleBar from "simplebar-react";

export enum InfiniteScrollDirection {
  TOP,
  BOTTOM
}

interface InfiniteScrollProps extends React.HTMLAttributes<HTMLElement> {
  onBottomReached: () => void;
  onTopReached: () => void;
  isBidirectional: boolean;
  elementHeight: number;
  onInitialFetch?: (elementCount?: number) => void;
  direction?: InfiniteScrollDirection;
  bottomThreshold: number;
  topThreshold: number;
  innerRef?: (ref: RefObject<HTMLDivElement>) => void;
  overrideScrollHeight?: number;
}

interface InfiniteScrollStore {}

/**
 * @deprecated use ant design instead
 */
export class InfiniteScroll extends Component<
  InfiniteScrollProps,
  InfiniteScrollStore
> {
  static defaultProps = {
    onBottomReached: (): void => {},
    onTopReached: (): void => {},
    isBidirectional: false,
    direction: InfiniteScrollDirection.BOTTOM,
    bottomThreshold: 10,
    topThreshold: 10,
    innerRef: (): void => {}
  };

  private scrollRef = createRef<HTMLDivElement>();

  componentDidMount(): void {
    const { innerRef } = this.props;
    if (innerRef) {
      innerRef(this.scrollRef);
    }
    if (isMobile) {
      this.scrollRef.current!.addEventListener(
        "scroll",
        this.handleScrollEvent
      );
    } else {
      this.scrollRef.current!.addEventListener("wheel", this.handleMouseWheel);
    }
    this.handleInitElementHeight();
  }

  componentWillUnmount(): void {
    if (isMobile) {
      this.scrollRef.current!.removeEventListener(
        "scroll",
        this.handleScrollEvent
      );
    } else {
      this.scrollRef.current!.removeEventListener(
        "wheel",
        this.handleMouseWheel
      );
    }
  }

  handleInfiniteScroll = (): void => {
    const { scrollTop, offsetTop, offsetHeight, scrollHeight } =
      this.scrollRef.current!;

    const { bottomThreshold, topThreshold } = this.props;
    const scrolledUp = scrollTop + offsetTop;
    const scrollableHeight = scrollHeight - offsetHeight;
    const percentageToTop = (scrolledUp / scrollableHeight) * 100;

    if (percentageToTop >= 100 - bottomThreshold) {
      this.handleBottomReached();
    } else if (percentageToTop <= topThreshold) {
      this.handleTopReached();
    }
  };

  handleBottomReached = (): void => {
    const { onBottomReached, isBidirectional, direction } = this.props;
    if (isBidirectional) {
      onBottomReached();
    } else if (direction === InfiniteScrollDirection.BOTTOM) {
      onBottomReached();
    }
  };

  handleTopReached = (): void => {
    const { onTopReached, isBidirectional, direction } = this.props;
    if (isBidirectional) {
      onTopReached();
    } else if (direction === InfiniteScrollDirection.TOP) {
      onTopReached();
    }
  };

  handleMouseWheel = (event: WheelEvent): void => {
    event.stopPropagation();
    const { offsetHeight, scrollHeight } = this.scrollRef.current!;

    if (offsetHeight < scrollHeight) {
      this.handleInfiniteScroll();
    } else if (event.deltaY < 0) {
      this.handleTopReached();
    } else if (event.deltaY > 0) {
      this.handleBottomReached();
    }
  };

  handleScrollEvent = (event: Event): void => {
    event.stopPropagation();
    const { offsetHeight, scrollHeight } = this.scrollRef.current!;

    if (offsetHeight < scrollHeight) {
      this.handleInfiniteScroll();
    }
  };

  handleInitElementHeight = (): void => {
    const { elementHeight, onInitialFetch, overrideScrollHeight } = this.props;
    const { scrollHeight } = this.scrollRef.current!;
    const initElementHeight = elementHeight;
    const actualScrollHeight = overrideScrollHeight
      ? window.innerHeight - overrideScrollHeight
      : scrollHeight;
    const elementCount = Math.floor(actualScrollHeight / initElementHeight) + 2;

    if (onInitialFetch) {
      onInitialFetch(elementCount);
    }
  };

  render(): JSX.Element {
    const {
      children,
      isBidirectional,
      onBottomReached,
      onTopReached,
      elementHeight,
      onInitialFetch,
      bottomThreshold,
      topThreshold,
      innerRef,
      overrideScrollHeight,
      ...htmlProps
    } = this.props;
    return (
      <SimpleBar
        onScroll={this.handleInfiniteScroll}
        className="simplebar-transparent"
        scrollableNodeProps={{
          ref: this.scrollRef
        }}
        {...htmlProps}
      >
        {children}
      </SimpleBar>
    );
  }
}

export default InfiniteScroll;
